import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Briefcase, Users as UsersIcon, Edit3, Archive, Shield, History, FileDown, EyeOff, Eraser, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function ClientDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { has, user } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("info");
  const [lookups, setLookups] = useState(null);
  const [showRel, setShowRel] = useState(false);
  const [editRel, setEditRel] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/subjects/${id}`);
      setS(r.data);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Errore"); }
  };

  useEffect(() => { load(); api.get("/lookups/all").then(r => setLookups(r.data)); }, [id]);

  const doArchive = async () => {
    const activeCount = (s?.practices || []).length;
    let force = false;
    if (activeCount > 0) {
      if (user?.role !== "admin") {
        alert(`Impossibile archiviare: ci sono ${activeCount} pratica/e collegata/e. Solo un amministratore può forzare l'archiviazione.`);
        return;
      }
      force = window.confirm(
        `⚠️ Attenzione: il cliente ha ${activeCount} pratica/e collegata/e. ` +
        "Come amministratore puoi forzare l'archiviazione (procedura amministrativa). Confermi?"
      );
      if (!force) return;
    } else {
      if (!window.confirm(`Archiviare "${s.display_name}"? Il record e tutte le relazioni verranno preservati; potrà essere ripristinato da un amministratore.`)) return;
    }
    try {
      await api.post(`/subjects/${id}/archive`, {}, { headers: force ? { "X-Admin-Force": "true" } : {} });
      nav("/clients");
    } catch { /* toast handled */ }
  };

  if (err) return <div className="ma-error">{err}</div>;
  if (!s) return <div className="ma-loading">Caricamento…</div>;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/clients">← Clienti</Link></div>
          <h1 className="ma-h1" data-testid="client-name">{s.display_name}</h1>
          <div className="ma-sub">
            <span className={`ma-pill ${s.subject_type==="company"?"pill-blue":"pill-gray"}`}>{s.subject_type==="company"?"Impresa":"Persona"}</span>
            <span className="ma-pill pill-status">{s.client_status}</span>
            {s.is_pseudonymized && <span className="ma-pill pill-warn">Pseudonimizzato</span>}
          </div>
        </div>
        <div className="ma-actions">
          {has("subjects.write") && <Link to={`/clients/${id}/edit`} className="ma-btn-secondary-sm" data-testid="btn-edit-client"><Edit3 size={14}/> Modifica</Link>}
          {has("subjects.delete") && <button className="ma-btn-danger-sm" onClick={doArchive} data-testid="btn-archive-client-detail"><Archive size={14}/> Archivia</button>}
        </div>
      </div>

      <div className="ma-tabs">
        <button className={`ma-tab ${tab==="info"?"active":""}`} onClick={()=>setTab("info")}>Informazioni</button>
        <button className={`ma-tab ${tab==="rel"?"active":""}`} onClick={()=>setTab("rel")} data-testid="tab-relationships">Relazioni ({s.relationships.length})</button>
        <button className={`ma-tab ${tab==="prac"?"active":""}`} onClick={()=>setTab("prac")}>Pratiche ({s.practices.length})</button>
      </div>

      {tab === "info" && (
        <div className="ma-info-grid">
          <div className="ma-info-block">
            <h3 className="ma-h3">Contatti</h3>
            <Row l="Email" v={s.email}/><Row l="PEC" v={s.pec}/><Row l="Telefono" v={s.phone}/>
            <Row l="Telefono secondario" v={s.second_phone}/>
            <Row l="Sito" v={s.website}/><Row l="CF" v={s.fiscal_code} mono/><Row l="PIVA" v={s.vat_number} mono/>
          </div>
          {s.person && (
            <div className="ma-info-block">
              <h3 className="ma-h3">Persona</h3>
              <Row l="Nome" v={s.person.first_name}/><Row l="Cognome" v={s.person.last_name}/>
              <Row l="Nascita" v={s.person.birth_date && new Date(s.person.birth_date).toLocaleDateString("it-IT")}/>
              <Row l="Luogo nascita" v={s.person.birth_place}/><Row l="Provincia/Stato" v={s.person.birth_province}/>
              <Row l="Sesso" v={s.person.gender}/><Row l="Nazionalità" v={s.person.nationality}/>
              <Row l="Professione" v={s.person.profession}/>
              <Row l="Documento" v={s.person.identity_document_type && `${s.person.identity_document_type} · ${s.person.identity_document_number || ""}`}/>
              <Row l="Scadenza doc." v={s.person.identity_document_expiry && new Date(s.person.identity_document_expiry).toLocaleDateString("it-IT")}/>
            </div>
          )}
          {s.company && (
            <div className="ma-info-block">
              <h3 className="ma-h3">Impresa</h3>
              <Row l="Denominazione" v={s.company.business_name}/>
              <Row l="Forma giuridica" v={s.company.legal_form}/>
              <Row l="Data costituzione" v={s.company.incorporation_date && new Date(s.company.incorporation_date).toLocaleDateString("it-IT")}/>
              <Row l="Inizio attività" v={s.company.activity_start_date && new Date(s.company.activity_start_date).toLocaleDateString("it-IT")}/>
              <Row l="Stato impresa" v={s.company.company_status}/>
              <Row l="ATECO principale" v={s.company.ateco_code} mono/>
              <Row l="ATECO secondari" v={(s.company.ateco_secondary || []).join(", ")} mono/>
              <Row l="Dimensione" v={s.company.company_size}/>
              <Row l="Dipendenti" v={s.company.employees_count}/>
              <Row l="Numero REA" v={s.company.rea_number}/>
              <Row l="Camera di commercio" v={s.company.chamber_of_commerce}/>
              <Row l="Capitale sociale" v={s.company.share_capital ? `€ ${s.company.share_capital}` : null}/>
              <Row l="Fatturato" v={s.company.revenue_last_year ? `€ ${s.company.revenue_last_year} (${s.company.revenue_year || "n.d."})` : null}/>
            </div>
          )}
          {(s.addresses && s.addresses.length > 0) && (
            <div className="ma-info-block ma-col-full">
              <h3 className="ma-h3">Indirizzi</h3>
              {s.addresses.map(a => (
                <div key={a.id} className="ma-addr-line">
                  <span className="ma-pill">{a.address_type}</span>
                  {a.is_primary && <span className="ma-pill pill-ok">Principale</span>}
                  <span>{[a.street, a.civic_number].filter(Boolean).join(" ")}, {a.zip} {a.city} ({a.province}) {a.region ? `— ${a.region}` : ""}</span>
                </div>
              ))}
            </div>
          )}
          <div className="ma-info-block ma-col-full">
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <h3 className="ma-h3" style={{margin:0}}><Shield size={14} style={{verticalAlign:"-2px", marginRight:6}}/> Privacy & Consenso</h3>
              {user?.role === "admin" && !s.is_anonymized && (
                <GdprActionsBar subject={s} onChange={load}/>
              )}
            </div>
            <Row l="Consenso" v={s.privacy_consent ? "Sì" : "No"}/>
            <Row l="Data consenso" v={s.privacy_consent_date && new Date(s.privacy_consent_date).toLocaleDateString("it-IT")}/>
            <Row l="Modalità/fonte" v={s.privacy_consent_source}/>
            <Row l="Data revoca" v={s.privacy_revoked_at && new Date(s.privacy_revoked_at).toLocaleDateString("it-IT")}/>
            <Row l="Note privacy" v={s.privacy_notes}/>
            {s.is_anonymized && <div className="ma-warning" style={{marginTop:8}}><AlertTriangle size={14}/> Soggetto anonimizzato il {s.gdpr_processed_at ? new Date(s.gdpr_processed_at).toLocaleDateString("it-IT") : "—"}.</div>}
            {s.is_pseudonymized && !s.is_anonymized && <div className="ma-warning" style={{marginTop:8}}><AlertTriangle size={14}/> Soggetto pseudonimizzato il {s.gdpr_processed_at ? new Date(s.gdpr_processed_at).toLocaleDateString("it-IT") : "—"}.</div>}
            <ConsentHistoryPanel subjectId={s.id}/>
          </div>
          <div className="ma-info-block ma-col-full">
            <h3 className="ma-h3">Note</h3>
            <div className="ma-notes">{s.notes || "—"}</div>
          </div>
        </div>
      )}

      {tab === "rel" && (
        <div>
          <div className="ma-actions" style={{marginBottom:12}}>
            {has("subjects.write") && (
              <button className="ma-btn-primary-sm" onClick={()=>setShowRel(true)} data-testid="btn-add-relationship">
                <Plus size={14}/> Nuova relazione
              </button>
            )}
          </div>
          <div className="ma-table-wrap">
            <table className="ma-table">
              <thead><tr><th>Da</th><th></th><th>A</th><th>Tipo</th><th>Ruolo</th><th>Quota %</th><th>Dal</th><th>Al</th><th>Attiva</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {s.relationships.length === 0 && <tr><td colSpan="11" className="ma-empty">Nessuna relazione.</td></tr>}
                {s.relationships.map(r => {
                  const isFromSelf = r.from_subject_id === s.id;
                  const otherName = r.other_subject?.display_name || "(soggetto non disponibile)";
                  const otherId = r.other_subject?.id;
                  const fromCell = isFromSelf ? s.display_name : otherName;
                  const toCell = isFromSelf ? otherName : s.display_name;
                  const fromLink = isFromSelf ? s.id : otherId;
                  const toLink = isFromSelf ? otherId : s.id;
                  return (
                    <tr key={r.id} data-testid={`rel-row-${r.id}`}>
                      <td>{fromLink ? <Link to={`/clients/${fromLink}`} className="ma-link">{fromCell}</Link> : fromCell}</td>
                      <td className="ma-arrow-cell">→</td>
                      <td>{toLink ? <Link to={`/clients/${toLink}`} className="ma-link">{toCell}</Link> : toCell}</td>
                      <td><span className="ma-pill">{r.type.label}</span></td>
                      <td>{r.role_label || "—"}</td>
                      <td>{r.ownership_percentage != null ? `${r.ownership_percentage}%` : "—"}</td>
                      <td>{r.start_date ? new Date(r.start_date).toLocaleDateString("it-IT") : "—"}</td>
                      <td>{r.end_date ? new Date(r.end_date).toLocaleDateString("it-IT") : "—"}</td>
                      <td>{r.is_active ? <span className="ma-pill pill-ok">Attiva</span> : <span className="ma-pill">Storica</span>}</td>
                      <td className="ma-truncate">{r.notes || "—"}</td>
                      <td className="ma-row-actions">
                        {has("subjects.write") && (
                          <button className="ma-btn-link" onClick={()=>setEditRel({
                            ...r,
                            type_id: r.type.id,
                            other_subject_id: isFromSelf ? r.to_subject_id : r.from_subject_id,
                            other_subject_type: r.other_subject?.subject_type,
                          })} data-testid={`btn-edit-rel-${r.id}`}>Modifica</button>
                        )}
                        {has("subjects.write") && r.is_active && <button className="ma-btn-link" onClick={async()=>{
                          if(window.confirm("Disattivare la relazione mantenendo lo storico?")){
                            await api.patch(`/subjects/relationships/${r.id}`, {is_active: false, end_date: new Date().toISOString().slice(0,10)});
                            load();
                          }
                        }} data-testid={`btn-deact-rel-${r.id}`}>Disattiva</button>}
                        {has("subjects.write") && <button className="ma-btn-link ma-danger" onClick={async()=>{
                          if(window.confirm("Eliminare definitivamente la relazione? Lo storico andrà perso.")){await api.delete(`/subjects/relationships/${r.id}`); load();}
                        }} data-testid={`btn-del-rel-${r.id}`}>Elimina</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {showRel && lookups && <RelationshipModal subject={s} lookups={lookups} onClose={()=>setShowRel(false)} onDone={()=>{setShowRel(false); load();}}/>}
          {editRel && lookups && <RelationshipModal subject={s} lookups={lookups} editing={editRel} onClose={()=>setEditRel(null)} onDone={()=>{setEditRel(null); load();}}/>}
        </div>
      )}

      {tab === "prac" && (
        <div>
          <div className="ma-actions" style={{marginBottom:12}}>
            <Link to={`/practices/new?subject=${s.id}`} className="ma-btn-primary-sm"><Plus size={14}/> Nuova pratica</Link>
          </div>
          <div className="ma-table-wrap">
            <table className="ma-table">
              <thead><tr><th>Codice</th><th>Titolo</th><th>Servizio</th><th>Scadenza</th></tr></thead>
              <tbody>
                {s.practices.length === 0 && <tr><td colSpan="4" className="ma-empty">Nessuna pratica.</td></tr>}
                {s.practices.map(p => (
                  <tr key={p.id}>
                    <td className="ma-mono"><Link to={`/practices/${p.id}`} className="ma-link">{p.code}</Link></td>
                    <td>{p.title}</td><td>{p.service_type}</td>
                    <td>{p.deadline ? new Date(p.deadline).toLocaleDateString("it-IT") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ l, v, mono }) => (
  <div className="ma-row-kv">
    <div className="ma-row-k">{l}</div>
    <div className={`ma-row-v ${mono?"ma-mono":""}`}>{v == null || v === "" ? "—" : String(v)}</div>
  </div>
);

// -------- GDPR: barra azioni admin (solo scheda cliente) --------
function GdprActionsBar({ subject, onChange }) {
  const [action, setAction] = useState(null); // 'export' | 'pseudo' | 'anon'
  const [preview, setPreview] = useState(null);

  const open = async (kind) => {
    setPreview(null);
    setAction(kind);
    if (kind === "pseudo") {
      try {
        const r = await api.post(`/gdpr/subjects/${subject.id}/pseudonymize-preview`);
        setPreview(r.data);
      } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore anteprima."); setAction(null); }
    } else if (kind === "anon") {
      try {
        const r = await api.post(`/gdpr/subjects/${subject.id}/anonymize-preview`);
        setPreview(r.data);
      } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore anteprima."); setAction(null); }
    }
  };

  const doExport = async (format) => {
    try {
      const r = await api.get(`/gdpr/subjects/${subject.id}/export`, {
        params: { format }, responseType: "blob",
      });
      const blob = new Blob([r.data], { type: format === "zip" ? "application/zip" : "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `gdpr_export_${subject.id}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} scaricato.`);
      setAction(null);
    } catch { toast.error("Errore export."); }
  };

  return (
    <div className="ma-row" style={{gap:6}}>
      <button className="ma-btn-secondary-sm" onClick={()=>open("export")} data-testid="btn-gdpr-export">
        <FileDown size={12}/> Esporta dati
      </button>
      {!subject.is_pseudonymized && (
        <button className="ma-btn-secondary-sm" onClick={()=>open("pseudo")} data-testid="btn-gdpr-pseudo">
          <EyeOff size={12}/> Pseudonimizza
        </button>
      )}
      <button className="ma-btn-danger-sm" onClick={()=>open("anon")} data-testid="btn-gdpr-anon">
        <Eraser size={12}/> Anonimizza
      </button>

      {action === "export" && (
        <div className="ma-modal-overlay" onClick={()=>setAction(null)}>
          <div className="ma-modal" onClick={e=>e.stopPropagation()}>
            <div className="ma-modal-head">Esporta dati soggetto</div>
            <div className="ma-form-panel">
              <p>Soggetto: <strong>{subject.display_name}</strong></p>
              <p className="ma-hint">L'export include anagrafica, pratiche, dati economici, metadati dei documenti, storico consenso e audit trail (nessun binario). Operazione loggata.</p>
              <div className="ma-form-actions" style={{gap:12, justifyContent:"center"}}>
                <button className="ma-btn-secondary" onClick={()=>setAction(null)}>Annulla</button>
                <button className="ma-btn-secondary" onClick={()=>doExport("json")} data-testid="btn-do-export-json"><FileDown size={14}/> JSON</button>
                <button className="ma-btn-primary" onClick={()=>doExport("zip")} data-testid="btn-do-export-zip"><FileDown size={14}/> ZIP</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(action === "pseudo" || action === "anon") && preview && (
        <IrreversibleModal
          kind={action === "pseudo" ? "pseudonymize" : "anonymize"}
          subject={subject}
          preview={preview}
          onClose={()=>{ setAction(null); setPreview(null); }}
          onDone={()=>{ setAction(null); setPreview(null); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function IrreversibleModal({ kind, subject, preview, onClose, onDone }) {
  const isAnon = kind === "anonymize";
  const label = isAnon ? "ANONIMIZZA" : "PSEUDONIMIZZA";
  const endpoint = isAnon ? `/gdpr/subjects/${subject.id}/anonymize`
                          : `/gdpr/subjects/${subject.id}/pseudonymize`;
  const [c1, setC1] = useState("");
  const [c2, setC2] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const can = c1 === label && c2 === subject.display_name && !busy;

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(endpoint, { confirm_action: c1, confirm_subject: c2, reason: reason || null });
      toast.success(isAnon ? "Anonimizzazione completata." : "Pseudonimizzazione completata.");
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Errore.");
    }
    setBusy(false);
  };

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal ma-modal-rel" onClick={e=>e.stopPropagation()} data-testid={`modal-${kind}`}>
        <div className="ma-modal-head"><AlertTriangle size={16} style={{verticalAlign:"-3px", marginRight:6, color:"#d97706"}}/> {isAnon ? "Anonimizzazione irreversibile" : "Pseudonimizzazione irreversibile"}</div>
        <div className="ma-form-panel">
          <div className="ma-warning" style={{marginBottom:12}}>
            <AlertTriangle size={16}/><span><strong>Operazione IRREVERSIBILE</strong> su: <strong>{subject.display_name}</strong>.</span>
          </div>
          {isAnon ? (
            <><h4 className="ma-h4">Effetti</h4>
            <ul>{(preview.effects || []).map((e,i)=><li key={i}>{e}</li>)}</ul>
            <div className="ma-hint">Pratiche preservate: {preview.practices_preserved} — Documenti (metadati): {preview.documents_metadata_preserved} — Relazioni disattivate: {preview.relationships_deactivated}</div></>
          ) : (
            <><h4 className="ma-h4">Campi che verranno redatti</h4>
            <ul className="ma-mono" style={{fontSize:12, maxHeight:180, overflow:"auto"}}>
              {Object.entries(preview.fields_to_redact || {}).map(([k,v])=>(<li key={k}><strong>{k}</strong>: {v == null ? <em>(vuoto)</em> : String(v).slice(0,80)}</li>))}
            </ul>
            <div className="ma-hint">Pratiche preservate: {preview.practices_preserved}</div></>
          )}
          <div className="ma-hint" style={{marginTop:12}}>{preview.notice}</div>
          <hr style={{margin:"16px 0"}}/>
          <h4 className="ma-h4">Doppia conferma</h4>
          <div className="ma-form-grid">
            <div className="ma-col-full">
              <label className="ma-label">1) Digita esattamente <code>{label}</code></label>
              <input className="ma-input ma-mono" value={c1} onChange={e=>setC1(e.target.value)} data-testid={`confirm-action-${kind}`}/>
            </div>
            <div className="ma-col-full">
              <label className="ma-label">2) Digita esattamente il nome: <code>{subject.display_name}</code></label>
              <input className="ma-input" value={c2} onChange={e=>setC2(e.target.value)} data-testid={`confirm-subject-${kind}`}/>
            </div>
            <div className="ma-col-full">
              <label className="ma-label">Motivo (facoltativo, verrà loggato)</label>
              <input className="ma-input" value={reason} onChange={e=>setReason(e.target.value)} data-testid={`reason-${kind}`}/>
            </div>
          </div>
          {err && <div className="ma-error" data-testid={`err-${kind}`}>{typeof err === "string" ? err : JSON.stringify(err)}</div>}
          <div className="ma-form-actions">
            <button className="ma-btn-secondary" onClick={onClose} disabled={busy}>Annulla</button>
            <button className={isAnon?"ma-btn-danger":"ma-btn-primary"} onClick={go} disabled={!can} data-testid={`submit-${kind}`}>
              {busy ? "Elaborazione…" : (isAnon ? "Anonimizza definitivamente" : "Pseudonimizza definitivamente")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentHistoryPanel({ subjectId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/gdpr/subjects/${subjectId}/consent-history`).then(r => setRows(r.data)).catch(()=>setRows([]));
  const toggle = () => { const n = !open; setOpen(n); if (n && rows === null) load(); };
  return (
    <div style={{marginTop:12}}>
      <button type="button" className="ma-btn-link" onClick={toggle} data-testid="btn-toggle-consent-history">
        <History size={12}/> {open ? "Nascondi" : "Mostra"} storico consenso
      </button>
      {open && (
        <div className="ma-table-wrap" style={{marginTop:8}} data-testid="consent-history-table">
          <table className="ma-table">
            <thead><tr><th>Data</th><th>Evento</th><th>Consenso</th><th>Fonte</th><th>Utente</th><th>Note</th></tr></thead>
            <tbody>
              {rows == null && <tr><td colSpan="6" className="ma-empty">Caricamento…</td></tr>}
              {rows && rows.length === 0 && <tr><td colSpan="6" className="ma-empty">Nessuna modifica registrata.</td></tr>}
              {rows && rows.map(h => (
                <tr key={h.id}>
                  <td>{h.changed_at ? new Date(h.changed_at).toLocaleString("it-IT") : "—"}</td>
                  <td><span className={`ma-pill ${h.event_type==="revoked"?"pill-warn":(h.event_type==="granted"?"pill-ok":"")}`}>{h.event_type}</span></td>
                  <td>{h.previous_consent === null ? "—" : (h.previous_consent ? "Sì" : "No")} → {h.new_consent ? "Sì" : "No"}</td>
                  <td>{h.consent_source || "—"}</td>
                  <td>{h.changed_by_email || "—"}</td>
                  <td className="ma-truncate">{h.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RelationshipModal({ subject, lookups, onClose, onDone, editing }) {
  const [otherType, setOtherType] = useState(editing ? editing.other_subject_type : (subject.subject_type === "person" ? "company" : "person"));
  const [otherOptions, setOtherOptions] = useState([]);
  const [otherId, setOtherId] = useState(editing ? editing.other_subject_id : "");
  const [typeId, setTypeId] = useState(editing ? editing.type_id : (lookups.relationship_types[0]?.id || ""));
  const [role, setRole] = useState(editing?.role_label || "");
  const [perc, setPerc] = useState(editing?.ownership_percentage != null ? String(editing.ownership_percentage) : "");
  const [startDate, setStartDate] = useState(editing?.start_date || "");
  const [endDate, setEndDate] = useState(editing?.end_date || "");
  const [isActive, setIsActive] = useState(editing ? editing.is_active : true);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const selectedType = lookups.relationship_types.find(t => t.id === typeId);
  const ownershipRelevant = selectedType && ["shareholder", "socio", "quotista", "azionista"].some(k =>
    (selectedType.code || "").toLowerCase().includes(k) || (selectedType.label || "").toLowerCase().includes(k)
  );

  useEffect(() => {
    if (editing) return; // don't reload when editing
    api.get(`/subjects?subject_type=${otherType}&limit=200`).then(r => {
      setOtherOptions((r.data.items || []).filter(x => x.id !== subject.id));
    });
  }, [otherType, subject.id, editing]);

  const validate = () => {
    if (!editing && !otherId) return "Seleziona la controparte";
    if (!typeId) return "Seleziona il tipo di relazione";
    if (perc !== "") {
      const n = parseFloat(perc);
      if (isNaN(n) || n < 0 || n > 100) return "La quota deve essere tra 0 e 100";
      // check max 2 decimals
      if (!/^\d+(\.\d{1,2})?$/.test(perc)) return "La quota accetta al massimo 2 decimali";
    }
    if (startDate && endDate && endDate < startDate) return "La data finale non può precedere quella iniziale";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setErr(v); return; }
    setBusy(true); setErr(null);
    try {
      const payload = {
        relationship_type_id: typeId,
        role_label: role || null,
        ownership_percentage: (ownershipRelevant && perc !== "") ? parseFloat(perc) : null,
        start_date: startDate || null,
        end_date: endDate || null,
        is_active: !!isActive,
        notes: notes || null,
      };
      if (editing) {
        await api.patch(`/subjects/relationships/${editing.id}`, payload);
      } else {
        await api.post("/subjects/relationships", {
          ...payload,
          from_subject_id: subject.id,
          to_subject_id: otherId,
        });
      }
      onDone();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Errore nel salvataggio"));
      setBusy(false);
    }
  };

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal ma-modal-rel" onClick={e=>e.stopPropagation()}>
        <div className="ma-modal-head">{editing ? "Modifica relazione" : "Nuova relazione"}</div>
        <form onSubmit={submit} className="ma-form">
          {!editing && (
            <div className="ma-form-grid">
              <div><label className="ma-label">Tipo controparte</label>
                <select className="ma-input" value={otherType} onChange={e=>setOtherType(e.target.value)}>
                  <option value="person">Persona</option>
                  <option value="company">Impresa</option>
                </select>
              </div>
              <div><label className="ma-label">Controparte *</label>
                <select className="ma-input" value={otherId} onChange={e=>setOtherId(e.target.value)} required data-testid="rel-target">
                  <option value="">— Seleziona —</option>
                  {otherOptions.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="ma-form-grid">
            <div><label className="ma-label">Tipo di relazione *</label>
              <select className="ma-input" value={typeId} onChange={e=>setTypeId(e.target.value)} required data-testid="rel-type">
                {lookups.relationship_types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div><label className="ma-label">Ruolo (etichetta libera)</label>
              <input className="ma-input" value={role} onChange={e=>setRole(e.target.value)}/>
            </div>
            {ownershipRelevant && (
              <div><label className="ma-label">Quota %</label>
                <input type="number" step="0.01" min="0" max="100" className="ma-input"
                        value={perc} onChange={e=>setPerc(e.target.value)}
                        placeholder="0.00–100.00" data-testid="rel-perc"/>
                <div className="ma-hint">Da 0 a 100, massimo 2 decimali.</div>
              </div>
            )}
            <div><label className="ma-label">Data iniziale</label>
              <input type="date" className="ma-input" value={startDate} onChange={e=>setStartDate(e.target.value)} data-testid="rel-start"/>
            </div>
            <div><label className="ma-label">Data finale</label>
              <input type="date" className="ma-input" value={endDate} onChange={e=>setEndDate(e.target.value)} data-testid="rel-end"/>
            </div>
            <div className="ma-col-full">
              <label className="ma-checkbox">
                <input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)} data-testid="rel-active"/>
                <span>Relazione attiva</span>
              </label>
            </div>
            <div className="ma-col-full">
              <label className="ma-label">Note</label>
              <textarea className="ma-input" rows={2} value={notes} onChange={e=>setNotes(e.target.value)} data-testid="rel-notes"/>
            </div>
          </div>
          {err && <div className="ma-error" data-testid="rel-error">{err}</div>}
          <div className="ma-form-actions">
            <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="rel-submit">
              {busy ? "Salvataggio…" : (editing ? "Salva modifiche" : "Crea relazione")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
